import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, PopoverMenu, SparkIcon, TooltipTrigger } from '@spark';
import { QUERY_FILE_LANGUAGES } from '@/features/trainings/utils/queryFileGenerator';
import QueryFileModal from '@/features/tasks/components/Task/QueryFileModal';

const GAP = 4;

/**
 * Playground toolbar control: open an SDK query-file snippet pointed at the
 * live GPU vLLM pod (`/public/playground`), with a language submenu.
 */
const DownloadQueryFileButton = ({
    clientId,
    usecaseId,
    taskName,
    projectName,
    taskType,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, language: 'python' });
    const [panelStyle, setPanelStyle] = useState({});
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    const updatePosition = useCallback(() => {
        const trigger = wrapRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        setPanelStyle({
            position: 'fixed',
            top: rect.bottom + GAP,
            right: Math.max(8, window.innerWidth - rect.right),
            zIndex: 1400,
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const id = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(id);
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const handleReposition = () => updatePosition();
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
        return () => {
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const menuItems = useMemo(
        () =>
            QUERY_FILE_LANGUAGES.map(({ key, label }) => ({
                label,
                suffixIconSlug: 'eye',
                onClick: () => {
                    setOpen(false);
                    setModal({ open: true, language: key });
                },
            })),
        []
    );

    return (
        <>
            <div ref={wrapRef} className="pg-query-file">
                <TooltipTrigger
                    text="Query this model through the SpecificAI SDK. Make sure the GPU is started before running the script."
                    placement="right"
                >
                    <span>
                        <Button
                            className="fs-unmask"
                            variant="secondary"
                            size="small"
                            disabled={disabled}
                            startIcon={<SparkIcon slug="code" size="small" />}
                            endIcon={<SparkIcon slug="chevron-down" size="small" />}
                            onClick={() => setOpen((value) => !value)}
                        >
                            Query file
                        </Button>
                    </span>
                </TooltipTrigger>
                {open
                    ? createPortal(
                        <div
                            ref={panelRef}
                            style={panelStyle}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <PopoverMenu tip={false} items={menuItems} />
                        </div>,
                        document.body
                    )
                    : null}
            </div>
            <QueryFileModal
                open={modal.open}
                language={modal.language}
                onClose={() => setModal((prev) => ({ ...prev, open: false }))}
                clientId={clientId}
                usecaseId={usecaseId}
                taskName={taskName || ''}
                projectName={projectName || ''}
                taskType={taskType}
                usePlaygroundIngress
            />
        </>
    );
};

export default DownloadQueryFileButton;
